import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import config from '../../config/config';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { getKLMenuItems } from '../../utils/menuItems';
import DataTable from 'react-data-table-component';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { FaEye, FaTrash } from "react-icons/fa";
import Modal from '../../components/common/Modal';
import '../../styles/App.css';
import Select from 'react-select';
import { Tooltip } from 'react-tooltip';

const DataPage = () => {
  const [dataList, setDataList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [ticketNoToDelete, setTicketNoToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);

  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/', { state: { sessionExpired: true } });
      }
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const decoded = jwtDecode(token);
        if (!token || !decoded.twoFactorVerified) {
          navigate('/');
          return;
        }

        const decodedToken = jwtDecode(token);
        setUsername(decodedToken.username);
        setUserId(decodedToken.userId);

        const response = await axios.get(`${config.API_URL}/data?userId=${decodedToken.userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const modifiedData = response.data.map(item => {
          if (item.status === 'Draft') {
            return { ...item, status: 'Sedang Verifikasi' };
          }
          return item;
        });

        setDataList(modifiedData);
        setFilteredData(modifiedData);
      } catch (err) {
        setError('Belum ada data yang diupload, Silahkan klik Tambah Data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const applyFilters = useCallback((data) => {
    return data.filter((row) => {
      return activeFilters.every((filter) => {
        const rowValue = row[filter.key]?.toString().toLowerCase() || '';
        return rowValue.includes(filter.value.toLowerCase());
      });
    });
  }, [activeFilters]);

  useEffect(() => {
    const filtered = applyFilters(dataList);
    setFilteredData(filtered);
  }, [activeFilters, dataList, applyFilters]);

  const handleSearch = () => {
    setSearchLoading(true);
    setTimeout(() => {
      const query = searchText.toLowerCase();
      if (query) {
        const filtered = dataList.filter((row) =>
          row.no_tiket.toLowerCase().includes(query) ||
          row.nama_igt.toLowerCase().includes(query) ||
          row.kl.toLowerCase().includes(query) ||
          row.jenis_igt?.toLowerCase().includes(query) ||
          row.status.toLowerCase().includes(query) ||
          row.verifikator?.toLowerCase().includes(query)
        );
        setFilteredData(filtered);
      } else {
        setFilteredData(dataList);
      }
      setSearchLoading(false);
    }, 500);
  };

  const handleDeleteClick = (ticketNo) => {
    setTicketNoToDelete(ticketNo);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_URL}/delete/data`, { ticketNo: ticketNoToDelete }, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setDataList(prevData => prevData.filter(item => item.no_tiket !== ticketNoToDelete));
      setFilteredData(prevData => prevData.filter(item => item.no_tiket !== ticketNoToDelete));
      setShowDeleteModal(false);
      setTicketNoToDelete(null);
      setSuccessMessage('Data berhasil dihapus!');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error delete ticket:', error);
    }
  };

  const ValueFilterDropdown = ({ column, data, onSelect }) => {
    const uniqueValues = [...new Set(data.map((row) => row[column]?.toString() || ''))].filter(Boolean);
    const options = uniqueValues.map((value) => ({
      value,
      label: value,
    }));

    return (
      <Select
        options={options}
        placeholder={`Pilih nilai untuk ${column}`}
        isSearchable
        onChange={(selectedOption) => onSelect(selectedOption?.value || '')}
        styles={{
          control: (provided) => ({
            ...provided,
            width: '250px',
          }),
        }}
      />
    );
  };

  const AddFilter = ({ onAddFilter, data }) => {
    const [selectedColumn, setSelectedColumn] = useState('');
    const [filterValue, setFilterValue] = useState('');

    const columns = [
      { key: 'no_tiket', label: 'No Tiket' },
      { key: 'nama_igt', label: 'Nama IGT' },
      { key: 'kl', label: 'KL' },
      { key: 'jenis_igt', label: 'Jenis IGT' },
      { key: 'status', label: 'Status' },
      { key: 'verifikator', label: 'Verifikator' },
    ];

    const handleAddFilter = () => {
      if (selectedColumn && filterValue) {
        onAddFilter({ key: selectedColumn, value: filterValue });
        setSelectedColumn('');
        setFilterValue('');
      }
    };

    return (
      <div className="add-filter-container mb-3 d-flex align-items-center gap-2">
        <select
          className="form-select"
          value={selectedColumn}
          onChange={(e) => setSelectedColumn(e.target.value)}
        >
          <option value="">Pilih Kolom</option>
          {columns.map((col) => (
            <option key={col.key} value={col.key}>
              {col.label}
            </option>
          ))}
        </select>

        {selectedColumn && (
          <ValueFilterDropdown
            column={selectedColumn}
            data={data}
            onSelect={(value) => setFilterValue(value)}
          />
        )}

        <button className="btn btn-primary d-flex align-items-center" onClick={handleAddFilter}>
          <i className="fas fa-plus me-2"></i>
        </button>
        <button
          className="btn btn-warning align-items-center"
          onClick={() => setActiveFilters([])}
          disabled={activeFilters.length === 0}
        >
          <i className="fas fa-sync-alt me-2"></i>
        </button>
      </div>
    );
  };

  const handleAddFilter = (newFilter) => {
    setActiveFilters((prevFilters) => [...prevFilters, newFilter]);
  };

  const handleRemoveFilter = (index) => {
    setActiveFilters((prevFilters) => prevFilters.filter((_, i) => i !== index));
  };

  // Fungsi untuk menentukan URL berdasarkan status dan role
  const getViewUrl = (row) => {
    const encodedTicketNo = btoa(row.no_tiket);

    if (row.status === 'Sudah Terintegrasi') {
      return `/rekapdata/${encodedTicketNo}`;
    } else {
      return `/kl/data/${btoa(row.no_tiket)}`;
    }
  };

  const columns = [
    {
      name: 'No Tiket',
      selector: row => row.no_tiket,
      sortable: true,
      width: '200px', // Atur lebar kolom
      cell: row => (
        <div data-tooltip-id="ticket-tooltip" data-tooltip-content={row.no_tiket}>
          {row.no_tiket.length > 20 ? `${row.no_tiket.substring(0, 20)}...` : row.no_tiket}
        </div>
      )
    },
    {
      name: 'Tanggal',
      selector: row => row.created_date,
      sortable: true,
      width: '150px',
      cell: row => {
        if (!row.created_date) return '-';
        const date = new Date(row.created_date);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('id-ID', {
          day: '2-digit',
          month: 'long',
          year: 'numeric'
        });
      },
      sortFunction: (rowA, rowB, direction) => {
        if (!rowA.created_date && !rowB.created_date) return 0;
        if (!rowA.created_date) return direction === 'asc' ? -1 : 1;
        if (!rowB.created_date) return direction === 'asc' ? 1 : -1;
        const dateA = new Date(rowA.created_date);
        const dateB = new Date(rowB.created_date);
        if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
        if (isNaN(dateA.getTime())) return direction === 'asc' ? -1 : 1;
        if (isNaN(dateB.getTime())) return direction === 'asc' ? 1 : -1;
        return direction === 'asc'
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }
    },
    {
      name: 'Nama IGT',
      selector: row => row.nama_igt,
      sortable: true,
      width: '200px',
      cell: row => (
        <div data-tooltip-id="nama-igt-tooltip" data-tooltip-content={row.nama_igt}>
          {row.nama_igt.length > 100 ? `${row.nama_igt.substring(0, 100)}...` : row.nama_igt}
        </div>
      )
    },
    {
      name: 'KL',
      selector: row => row.kl,
      sortable: true,
      width: '200px',
      cell: row => (
        <div data-tooltip-id="kl-tooltip" data-tooltip-content={row.kl}>
          {row.kl.length > 50 ? `${row.kl.substring(0, 50)}...` : row.kl}
        </div>
      )
    },
    {
      name: 'Jenis IGT',
      selector: row => row.jenis_igt || '-',
      sortable: true,
      width: '150px',
      cell: row => (
        <div data-tooltip-id="jenis-igt-tooltip" data-tooltip-content={row.jenis_igt || '-'}>
          {row.jenis_igt && row.jenis_igt.length > 30 ? `${row.jenis_igt.substring(0, 30)}...` : row.jenis_igt || '-'}
        </div>
      )
    },
    {
      name: 'Verifikator',
      selector: row => row.verifikator || '-',
      sortable: true,
      width: '150px',
      cell: row => (
        <div data-tooltip-id="verifikator-tooltip" data-tooltip-content={row.verifikator || '-'}>
          {row.verifikator && row.verifikator.length > 30 ? `${row.verifikator.substring(0, 30)}...` : row.verifikator || '-'}
        </div>
      )
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '150px',
      cell: row => {
        const statusClass = {
          'Sesuai': 'status-sesuai',
          'Tidak Sesuai': 'status-tidak-sesuai',
          'Perlu Perbaikan': 'status-perlu-perbaikan',
          'Sedang Verifikasi': 'status-sedang-verifikasi',
          'Sudah Terintegrasi': 'status-sudah-terintegrasi',
          'Belum Diverifikasi': 'status-belum-diverifikasi',
          'Revisi Pengajuan': 'status-revisi-pengajuan',
          'Disetujui': 'status-disetujui'
        }[row.status] || 'badge';

        return <span className={`badge ${statusClass}`}>{row.status}</span>;
      },
    },
    {
      name: 'Status Publikasi',
      selector: row => row.status_publikasi,
      sortable: true,
      width: '170px',
      cell: row => {
        if (row.jenis_igt === 'Renaksi PKSP') {
          const statusPublikasiClass = {
            'Siap Publikasi': 'status-publikasi-siap',
            'Belum Publikasi': 'status-publikasi-belum',
            'Sudah Publikasi': 'status-publikasi-sudah'
          }[row.status_publikasi] || 'status-publikasi-default';

          const displayText = row.status_publikasi || '-';

          return (
            <span
              className={`badge ${statusPublikasiClass}`}
              data-tooltip-id="status-publikasi-tooltip"
              data-tooltip-content={displayText}
            >
              {displayText}
            </span>
          );
        }
        return <span className="badge status-publikasi-default">-</span>;
      },
    },
    {
      name: 'Aksi',
      cell: row => (
        <div className="actions">
          <Link to={getViewUrl(row)} className="action-button view-button">
            <FaEye /> Lihat
          </Link>
          <button className="action-button delete-button" onClick={() => handleDeleteClick(row.no_tiket)}>
            <FaTrash />
            <span>Hapus</span>
          </button>
        </div>
      ),
      width: '150px', // Atur lebar kolom
    },
  ];

  const customSort = (rows, selector, direction) => {
    return rows.sort((a, b) => {
      const aField = selector(a) || '';
      const bField = selector(b) || '';
      // If both are dates, compare directly as dates
      if (selector.name === 'created_date') {
        if (!aField || !bField) {
          if (!aField && !bField) return 0;
          if (!aField) return direction === 'asc' ? 1 : -1;
          return direction === 'asc' ? -1 : 1;
        }
        const aDate = new Date(aField);
        const bDate = new Date(bField);

        if (direction === 'asc') {
          return aDate - bDate;
        } else {
          return bDate - aDate;
        }
      }
      if (direction === 'asc') {
        return aField.toString().localeCompare(bField.toString());
      } else {
        return bField.toString().localeCompare(aField.toString());
      }
    });
  };

  return (
    <DashboardLayout menuItems={getKLMenuItems()} userRole="Pemerintah">
      <div className="container mt-4">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/kl">Dashboard</a></li>
            <li className="breadcrumb-item active" aria-current="page">Daftar Data</li>
          </ol>
        </nav>
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <Link to="/kl/upload" className="btn btn-success">+ Tambah Data</Link>
          </div>
        </div>
        <div className="card shadow-lg p-4">
          <h3 className="title">Daftar Data</h3>

          {error && <div className="alert-danger">{error}</div>}
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <div className="search-container">
              <input
                type="text"
                className="filter-input"
                placeholder="Cari No Tiket, Nama, KL, atau Status"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className="btn btn-primary search-button"
                onClick={handleSearch}
                disabled={searchLoading}
              >
                <i className="fas fa-search"></i> Cari
              </button>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={applyFilters(filteredData)}
            progressPending={loading}
            progressComponent={<LoadingSpinner message="Memuat data..." type="spinner" size="medium" />}
            pagination
            highlightOnHover
            pointerOnHover
            defaultSortFieldId="created_date"
            defaultSortAsc={false}
            sortFunction={customSort}
            noDataComponent={<p className="text-center text-muted">Tidak ada data yang tersedia.</p>}
            responsive
          />
          <Tooltip id="ticket-tooltip" />
          <Tooltip id="nama-igt-tooltip" />
          <Tooltip id="kl-tooltip" />
          <Tooltip id="verifikator-tooltip" />
          <Tooltip id="jenis-igt-tooltip" />
        </div>

        {searchLoading && (
          <div className="loading-overlay-fullscreen">
            <LoadingSpinner message="Mencari data..." type="dots" size="large" />
          </div>
        )}
        {/* Modal Konfirmasi Hapus */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          type="confirm"
          title="Konfirmasi Hapus"
          message={`Apakah Anda yakin ingin menghapus tiket No ${ticketNoToDelete}? Tindakan ini tidak dapat dibatalkan.`}
          onConfirm={handleDeleteConfirm}
        />

        {/* Modal Sukses */}
        <Modal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          type="success"
          title="Berhasil"
          message={successMessage}
        />
      </div>
    </DashboardLayout>
  );
};

export default DataPage;